import React, { useState } from 'react';
import {
  Box,
  TextField,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
  IconButton,
  Alert,
  Button,
  Chip,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  CheckCircle,
  Cancel,
  InfoOutlined as InfoOutlinedIcon,
} from '@mui/icons-material';
import { SkillAuthConfig, SkillAuthType, TestResult } from '../types/skill';

// ─── Constants ────────────────────────────────────────────────────────────────

const AUTH_TYPE_LABELS: Record<SkillAuthType, string> = {
  none:          'None / Public',
  api_key:       'API Key',
  oauth:         'Bearer Token (OAuth)',
  oauth2:        'OAuth2 Client Credentials',
  basic:         'Basic Auth',
  custom_header: 'Custom Header',
  sigv4:         'AWS IAM (SigV4)',
  oci_signing:   'OCI API Signing',
  odoo_session:  'Odoo Service Login',
  // legacy type names kept for backward compat with existing saved skills
  oauth2_bearer: 'Bearer Token (OAuth)',
  basic_auth:    'Basic Auth',
};

const AUTH_TYPE_DESCRIPTIONS: Partial<Record<SkillAuthType, string>> = {
  none:          'No authentication required — your API endpoint is publicly accessible. Use this for internal services on a trusted network.',
  api_key:       'Sends a static key in a request header (e.g. X-API-Key). Get your API key from your service\'s developer or settings page.',
  oauth:         'Sends a Bearer token in the Authorization header. Get your access token from your OAuth provider\'s token endpoint or developer console.',
  oauth2:        'Automatically fetches a short-lived token using Client Credentials flow. Get Client ID and Secret from your OAuth2 provider\'s application settings.',
  basic:         'Sends username and password encoded in the Authorization header. Use the credentials for your API service account.',
  custom_header: 'Sends a custom key-value pair as a request header. Use when your API expects a non-standard auth header like X-Auth-Token.',
  sigv4:         'Signs requests using AWS IAM credentials. Get Access Key ID and Secret from AWS IAM Console → Users → Security credentials → Access keys.',
  oci_signing:   'Signs requests using OCI API key signing. Get credentials from OCI Console → Identity → Users → API Keys.',
  odoo_session:  'Exchanges an Odoo login and password for a long-lived session token via the Odoo MCP server\'s headless token endpoint. The password is used once, server-side, and never re-sent from your browser.',
};

// Only canonical types are shown in the dropdown — legacy aliases are excluded to avoid duplicates.
// AUTH_TYPE_LABELS still covers them so existing saved skills display the correct label.
const AUTH_TYPE_DROPDOWN: SkillAuthType[] = [
  'none', 'api_key', 'oauth', 'oauth2', 'basic', 'custom_header', 'sigv4', 'oci_signing', 'odoo_session',
];

const AWS_SERVICES = ['ec2', 's3', 'execute-api', 'bedrock', 'lambda', 'sts', 'sqs', 'sns', 'secretsmanager'] as const;

// ─── Props ────────────────────────────────────────────────────────────────────

export interface SkillAuthConfigFormProps {
  value: SkillAuthConfig;
  onChange: (config: SkillAuthConfig) => void;
  onTestConnection?: () => Promise<TestResult>;
}

// ─── Component ────────────────────────────────────────────────────────────────

const SkillAuthConfigForm: React.FC<SkillAuthConfigFormProps> = ({
  value,
  onChange,
  onTestConnection,
}) => {
  const [showFields, setShowFields] = useState<Record<string, boolean>>({});
  const [testing, setTesting]       = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);

  const update = (partial: Partial<SkillAuthConfig>) => {
    onChange({ ...value, ...partial });
    setTestResult(null);
  };

  const toggleShow = (field: string) =>
    setShowFields(prev => ({ ...prev, [field]: !prev[field] }));

  const handleTest = async () => {
    if (!onTestConnection) return;
    setTesting(true);
    setTestResult(null);
    try {
      const result = await onTestConnection();
      setTestResult(result);
    } finally {
      setTesting(false);
    }
  };

  // ── Reusable field builders ────────────────────────────────────────────────

  const secretField = (
    label: string,
    field: keyof SkillAuthConfig,
    placeholder?: string,
    infoTooltip?: string,
  ) => (
    <TextField
      key={field}
      label={label}
      size="small"
      fullWidth
      type={showFields[field] ? 'text' : 'password'}
      value={(value[field] as string) ?? ''}
      placeholder={placeholder}
      onChange={e => update({ [field]: e.target.value } as Partial<SkillAuthConfig>)}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            {infoTooltip && (
              <Tooltip title={infoTooltip}>
                <IconButton size="small" aria-label="More info" sx={{ p: 0.25, color: 'text.secondary' }}>
                  <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
            <IconButton size="small" onClick={() => toggleShow(field)} edge="end">
              {showFields[field] ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );

  const plainField = (
    label: string,
    field: keyof SkillAuthConfig,
    placeholder?: string,
    infoTooltip?: string,
  ) => (
    <TextField
      key={field}
      label={label}
      size="small"
      fullWidth
      value={(value[field] as string) ?? ''}
      placeholder={placeholder}
      onChange={e => update({ [field]: e.target.value } as Partial<SkillAuthConfig>)}
      InputProps={infoTooltip ? {
        endAdornment: (
          <InputAdornment position="end">
            <Tooltip title={infoTooltip}>
              <IconButton size="small" aria-label="More info" sx={{ p: 0.25, color: 'text.secondary' }}>
                <InfoOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </InputAdornment>
        ),
      } : undefined}
    />
  );

  // ── Per-type field rendering ───────────────────────────────────────────────

  const renderFields = () => {
    switch (value.type) {
      case 'api_key':
        return (
          <>
            {plainField(
              'Key Header Name', 'key_name', 'X-API-Key',
              'The HTTP header name your API expects the key in — e.g. X-API-Key, Authorization, X-Access-Token. Check your API\'s documentation.',
            )}
            {secretField(
              'Key Value', 'key_value', '••••••••••••••••',
              'The actual API key value. Find this in your service\'s developer settings or API credentials page.',
            )}
          </>
        );

      case 'oauth':
        return (
          <>
            {secretField(
              'Access Token', 'access_token', '••••••••••••••••',
              'The Bearer token included in the Authorization header. Get it from your OAuth provider after completing the OAuth flow.',
            )}
            {secretField(
              'Refresh Token', 'refresh_token', '•••••••••••••••• (optional)',
              'Optional. Used to automatically get a new access token when the current one expires. Get it from your OAuth provider alongside the access token.',
            )}
            {plainField(
              'Token URL', 'token_url', 'https://...',
              'Optional. The endpoint used to refresh the access token automatically. Find it in your OAuth provider\'s documentation — often labeled "Token endpoint".',
            )}
          </>
        );

      case 'oauth2':
        return (
          <>
            {plainField(
              'Token URL', 'token_url', 'https://...',
              'The endpoint where the skill fetches a new token using client credentials. Find it in your OAuth2 provider\'s documentation — often labeled "Token endpoint".',
            )}
            {plainField(
              'Client ID', 'client_id', undefined,
              'The public identifier for your application. Find it in your OAuth2 provider\'s application or client settings page.',
            )}
            {secretField(
              'Client Secret', 'client_secret', '••••••••••••••••',
              'The secret key for your application. Find it in your OAuth2 provider\'s application settings — treat it like a password.',
            )}
            {plainField(
              'Scope', 'scope', 'read write',
              'Optional. Space-separated list of permissions the token should grant — e.g. read write. Check your provider\'s API docs for valid scope values.',
            )}
            {plainField(
              'Audience', 'audience', '',
              'Optional. The intended recipient of the token — required by some providers like Auth0. Check your provider\'s documentation for the correct audience value.',
            )}
          </>
        );

      case 'basic':
        return (
          <>
            {plainField(
              'Username', 'username', undefined,
              'The username for HTTP Basic Auth. Use the account credentials for your API service.',
            )}
            {secretField(
              'Password', 'password', '••••••••••••••••',
              'The password for HTTP Basic Auth. Use the account credentials for your API service.',
            )}
          </>
        );

      case 'custom_header':
        return (
          <>
            {plainField(
              'Header Name', 'header_name', 'X-Custom-Header',
              'The name of the custom HTTP header your API expects — e.g. X-Auth-Token, X-Api-Secret. Check your API documentation.',
            )}
            {secretField(
              'Header Value', 'header_value', '••••••••••••••••',
              'The value to send in the custom header. Find it in your API\'s credentials or settings page.',
            )}
          </>
        );

      case 'sigv4':
        return (
          <>
            {plainField(
              'AWS Access Key ID', 'aws_access_key_id', 'AKIA...',
              'Found in AWS IAM Console → Users → your user → Security credentials → Access keys. Create a dedicated IAM user with only the permissions this skill needs.',
            )}
            {secretField(
              'AWS Secret Access Key', 'aws_secret_access_key', '•••••••••••••••',
              'Generated alongside the Access Key ID. Copy it immediately when creating the key — it is shown only once in AWS IAM Console.',
            )}
            {plainField(
              'AWS Region', 'aws_region', 'ap-south-1',
              'The AWS region where your service is deployed — e.g. ap-south-1, us-east-1. Find it in the AWS Console URL or your service\'s configuration.',
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>AWS Service</InputLabel>
                <Select
                  value={value.aws_service ?? ''}
                  label="AWS Service"
                  onChange={e => update({ aws_service: e.target.value })}
                >
                  {AWS_SERVICES.map(s => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Tooltip title="The AWS service this skill will call. Select the one matching your API endpoint — e.g. execute-api for API Gateway, s3 for S3, bedrock for Amazon Bedrock.">
                <IconButton size="small" aria-label="More info" sx={{ p: 0.25, color: 'text.secondary' }}>
                  <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>
            <Alert severity="warning" icon={false} sx={{ fontSize: '0.8rem' }}>
              These credentials must be scoped to the minimum IAM permissions this skill needs.
              Ask your AWS admin to create a dedicated IAM user for this skill.
            </Alert>
          </>
        );

      case 'oci_signing':
        return (
          <>
            {plainField(
              'Tenancy OCID', 'tenancy_ocid', 'ocid1.tenancy.oc1...',
              'Found in OCI Console → Administration → Tenancy details → OCID field.',
            )}
            {plainField(
              'User OCID', 'user_ocid', 'ocid1.user.oc1...',
              'Found in OCI Console → Identity → Users → click your user → OCID field.',
            )}
            {plainField(
              'Fingerprint', 'fingerprint', 'aa:bb:cc:dd:...',
              'The fingerprint of the API key pair. Found in OCI Console → Identity → Users → your user → API Keys after uploading your public key.',
            )}
            {plainField(
              'Region', 'region', 'ap-mumbai-1',
              'The OCI region identifier — e.g. ap-mumbai-1, us-ashburn-1. Found in OCI Console → top-right region selector.',
            )}
            <TextField
              label="Private Key (PEM)"
              size="small"
              fullWidth
              multiline
              rows={4}
              type={showFields['private_key_pem'] ? 'text' : 'password'}
              value={value.private_key_pem ?? ''}
              placeholder="-----BEGIN RSA PRIVATE KEY-----"
              helperText="Reveals on focus, masks on blur"
              onChange={e => update({ private_key_pem: e.target.value })}
              onFocus={() => setShowFields(prev => ({ ...prev, private_key_pem: true }))}
              onBlur={() => setShowFields(prev => ({ ...prev, private_key_pem: false }))}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="The private key (.pem file) of the API key pair you uploaded to OCI. Generate a key pair with: openssl genrsa -out oci_api_key.pem 2048 — then upload the public key in OCI Console → Identity → Users → your user → API Keys.">
                      <IconButton size="small" aria-label="More info" sx={{ p: 0.25, color: 'text.secondary' }}>
                        <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
            <Alert severity="warning" icon={false} sx={{ fontSize: '0.8rem' }}>
              Use a dedicated OCI IAM user with only network-security-groups manage permission.
            </Alert>
          </>
        );

      case 'odoo_session':
        return (
          <>
            {plainField(
              'Issue-Token URL', 'issue_token_url', 'https://odoo-mcp.example.com/auth/issue-token',
              'The Odoo MCP server\'s headless token endpoint. Ask your Odoo MCP admin for this URL.',
            )}
            {plainField(
              'Odoo Login', 'login', 'svc-prism7@example.com',
              'The dedicated Odoo service-account login. Do not use a real person\'s account.',
            )}
            {secretField(
              'Odoo Password', 'password', '••••••••••••••••',
              'The service account\'s password. Used once to obtain a session token — never stored or re-sent from the browser.',
            )}
            {plainField(
              'Database (optional)', 'db', '',
              'The Odoo database name, if the server hosts multiple databases.',
            )}
            <Alert severity="warning" icon={false} sx={{ fontSize: '0.8rem' }}>
              Use a dedicated Odoo service user with scoped ACLs — never a real person's login.
            </Alert>
          </>
        );

      default:
        return null;
    }
  };

  // ── Additional headers (additive, independent of Auth Type) ───────────────

  const extraHeaders = value.extra_headers ?? [];

  const updateExtraHeader = (index: number, partial: Partial<{ name: string; value: string }>) => {
    const next = extraHeaders.map((h, i) => (i === index ? { ...h, ...partial } : h));
    update({ extra_headers: next });
  };

  const addExtraHeader = () => {
    update({ extra_headers: [...extraHeaders, { id: crypto.randomUUID(), name: '', value: '' }] });
  };

  const removeExtraHeader = (index: number) => {
    update({ extra_headers: extraHeaders.filter((_, i) => i !== index) });
  };

  const renderExtraHeaders = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
          Additional Headers
        </Typography>
        <Tooltip title="Extra headers sent on every request alongside whichever Auth Type is selected above — e.g. X-MCP-Toolsets, X-Tenant-ID. Optional. Authorization and Content-Type are reserved and cannot be overridden here.">
          <IconButton size="small" aria-label="More info" sx={{ p: 0.25, color: 'text.secondary' }}>
            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {extraHeaders.map((h, i) => (
        <Box key={h.id ?? i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            label="Header Name"
            size="small"
            fullWidth
            value={h.name}
            placeholder="X-MCP-Toolsets"
            onChange={e => updateExtraHeader(i, { name: e.target.value })}
          />
          <TextField
            label="Header Value"
            size="small"
            fullWidth
            value={h.value}
            placeholder="repos,pull_requests,actions"
            onChange={e => updateExtraHeader(i, { value: e.target.value })}
          />
          <IconButton size="small" aria-label="Remove header" onClick={() => removeExtraHeader(i)}>
            <Cancel fontSize="small" />
          </IconButton>
        </Box>
      ))}

      <Button size="small" onClick={addExtraHeader} sx={{ alignSelf: 'flex-start' }}>
        + Add header
      </Button>
    </Box>
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
          Authentication
        </Typography>
        <Tooltip title="If your API requires a key or token to accept requests, configure it here. Credentials are encrypted and never visible after saving.">
          <IconButton size="small" aria-label="More info" sx={{ p: 0.25, color: 'text.secondary' }}>
            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Auth type selector */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <FormControl size="small" fullWidth>
          <InputLabel>Auth Type</InputLabel>
          <Select
            value={value.type}
            label="Auth Type"
            onChange={e => update({ type: e.target.value as SkillAuthType })}
          >
            {AUTH_TYPE_DROPDOWN.map(val => (
              <MenuItem key={val} value={val}>{AUTH_TYPE_LABELS[val]}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {AUTH_TYPE_DESCRIPTIONS[value.type] && (
          <Tooltip title={AUTH_TYPE_DESCRIPTIONS[value.type]}>
            <IconButton size="small" aria-label="More info" sx={{ p: 0.25, color: 'text.secondary' }}>
              <InfoOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Per-type fields */}
      {renderFields()}

      {/* Additional headers — additive on top of whichever Auth Type is selected */}
      {renderExtraHeaders()}

      {/* Test Connection */}
      {onTestConnection && value.type !== 'none' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleTest}
            disabled={testing}
            startIcon={
              testing ? <CircularProgress size={14} color="inherit" /> : undefined
            }
          >
            {testing ? 'Testing…' : 'Test Connection'}
          </Button>

          {testResult !== null && (
            testResult.success ? (
              <Chip
                icon={<CheckCircle />}
                label="Connected"
                color="success"
                size="small"
              />
            ) : (
              <Chip
                icon={<Cancel />}
                label={`Failed — ${testResult.error ?? 'Unknown error'}`}
                color="error"
                size="small"
                sx={{ maxWidth: 320 }}
              />
            )
          )}
        </Box>
      )}
    </Box>
  );
};

export default SkillAuthConfigForm;
